import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Search, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { useMembershipTemplates } from "@/hooks/use-membership-templates";
import {
  MembershipTemplate,
  MembershipTemplateFormData,
} from "@/constants";

const getDefaultFormData = (): MembershipTemplateFormData => ({
  name: "",
  durationMonths: 1,
  priceCouples: 0,
  priceSingleLadies: 0,
  priceSingleMales: 0,
  isActive: true,
});

const formatPrice = (price: string | number) => {
  const numPrice = typeof price === "string" ? parseFloat(price) : price;
  return numPrice === 0 ? "FREE" : `$${numPrice.toFixed(2)}`;
};

const formatDuration = (months: number) => {
  if (months === 1) return "1 Month";
  if (months < 12) return `${months} Months`;
  const years = months / 12;
  if (years === 1) return "1 Year";
  return `${years} Years`;
};

type MembershipState = {
  searchQuery?: string;
  currentPage?: number;
  itemsPerPage?: number;
};

const defaultState: MembershipState = {
  searchQuery: "",
  currentPage: 1,
  itemsPerPage: 10,
};

const MembershipTemplatesPage: React.FC = () => {
  useEffect(() => {
    document.title = "Membership Templates | SECH Admin";
  }, []);

  const [pageState, setPageState] = usePagePersistentState<MembershipState>("membershipTemplates", defaultState);
  const searchQuery = pageState.searchQuery;
  const currentPage = pageState.currentPage;
  const itemsPerPage = pageState.itemsPerPage;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "membership_templates_table",
    defaultColumn: "name",
    defaultDirection: "asc",
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MembershipTemplate | null>(null);
  const [formData, setFormData] = useState<MembershipTemplateFormData>(getDefaultFormData());
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MembershipTemplate | null>(null);

  const {
    templates,
    isLoading,
    isSubmitting,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleActive,
  } = useMembershipTemplates();

  const resetForm = () => setFormData(getDefaultFormData());

  const handleCreate = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (template: MembershipTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      durationMonths: template.durationMonths,
      priceCouples: parseFloat(template.priceCouples) || 0,
      priceSingleLadies: parseFloat(template.priceSingleLadies) || 0,
      priceSingleMales: parseFloat(template.priceSingleMales) || 0,
      isActive: template.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleSubmit = async (isEdit: boolean = false) => {
    if (!formData.name || formData.durationMonths < 1) return;
    let success: boolean;
    if (isEdit && editingTemplate) {
      success = await updateTemplate(editingTemplate.id, formData);
    } else {
      success = await createTemplate(formData);
    }
    if (success) {
      setIsCreateDialogOpen(false);
      setIsEditDialogOpen(false);
      setEditingTemplate(null);
      resetForm();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteTemplate(deleteTarget.id);
    setDeleteModal(false);
    setDeleteTarget(null);
  };

  const tableColumns = useMemo(() => [
    {
      label: "Name", field: "name", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-medium">{value}</span>
      ),
    },
    {
      label: "Duration", field: "durationMonths", sortable: true,
      cellTemplate: (value: number) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{formatDuration(value)}</span>
      ),
    },
    {
      label: "Couples", field: "priceCouples", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{formatPrice(value)}</span>
      ),
    },
    {
      label: "Single Ladies", field: "priceSingleLadies", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{formatPrice(value)}</span>
      ),
    },
    {
      label: "Single Males", field: "priceSingleMales", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{formatPrice(value)}</span>
      ),
    },
    {
      label: "Status", field: "isActive",
      cellTemplate: (value: boolean) => (
        <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${value ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
          <span className={`h-2 w-2 rounded-full mr-1 ${value ? "bg-green-800" : "bg-gray-500"} inline-flex items-center justify-center`} />
          <span className="leading-5 text-[13px] font-normal">{value ? "Active" : "Inactive"}</span>
        </span>
      ),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: MembershipTemplate) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: MembershipTemplate) => handleEdit(r) },
            { label: row.isActive ? "Deactivate" : "Activate", icon: row.isActive ? Trash2 : Pencil, onClick: (r: MembershipTemplate) => toggleActive(r.id, !r.isActive) },
            { label: "Delete", icon: Trash2, onClick: (r: MembershipTemplate) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], []);

  const filteredTemplates = useMemo(() => {
    const search = debouncedSearch.toLowerCase();
    if (!search) return templates;
    return templates.filter((t) =>
      t.name?.toLowerCase().includes(search)
      || formatDuration(t.durationMonths).toLowerCase().includes(search)
    );
  }, [templates, debouncedSearch]);

  const sortedTemplates = useMemo(() => {
    return [...filteredTemplates].sort((a: MembershipTemplate, b: MembershipTemplate) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "name": aVal = a.name ?? ""; bVal = b.name ?? ""; break;
        case "durationMonths": aVal = a.durationMonths ?? 0; bVal = b.durationMonths ?? 0; break;
        case "priceCouples": aVal = parseFloat(a.priceCouples) || 0; bVal = parseFloat(b.priceCouples) || 0; break;
        case "priceSingleLadies": aVal = parseFloat(a.priceSingleLadies) || 0; bVal = parseFloat(b.priceSingleLadies) || 0; break;
        case "priceSingleMales": aVal = parseFloat(a.priceSingleMales) || 0; bVal = parseFloat(b.priceSingleMales) || 0; break;
        default: aVal = a.name ?? ""; bVal = b.name ?? "";
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredTemplates, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedTemplates.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedTemplates = sortedTemplates.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setPageState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setPageState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedTemplates.length, handlePageChange, handleItemsPerPageChange };

  const renderTemplateForm = (isEdit: boolean) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <Label htmlFor="name">Template Name *</Label>
        <Input id="name" value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g., 1 Month" />
      </div>
      <div>
        <Label htmlFor="durationMonths">Duration (Months) *</Label>
        <Input id="durationMonths" type="number" min="1" value={formData.durationMonths} onChange={(e) => setFormData((prev) => ({ ...prev, durationMonths: parseInt(e.target.value) || 1 }))} />
      </div>
      <div className="space-y-3">
        <Label>Pricing (USD)</Label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="priceCouples" className="text-xs text-muted-foreground">Couples</Label>
            <Input id="priceCouples" type="number" min="0" step="0.01" value={formData.priceCouples} onChange={(e) => setFormData((prev) => ({ ...prev, priceCouples: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <Label htmlFor="priceSingleLadies" className="text-xs text-muted-foreground">Single Ladies</Label>
            <Input id="priceSingleLadies" type="number" min="0" step="0.01" value={formData.priceSingleLadies} onChange={(e) => setFormData((prev) => ({ ...prev, priceSingleLadies: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <Label htmlFor="priceSingleMales" className="text-xs text-muted-foreground">Single Males</Label>
            <Input id="priceSingleMales" type="number" min="0" step="0.01" value={formData.priceSingleMales} onChange={(e) => setFormData((prev) => ({ ...prev, priceSingleMales: parseFloat(e.target.value) || 0 }))} />
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Switch id="isActive" checked={formData.isActive} onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))} />
        <Label htmlFor="isActive">Active</Label>
      </div>
      <div className="flex justify-end space-x-2 pt-4">
        <Button variant="outline" onClick={() => { setIsCreateDialogOpen(false); setIsEditDialogOpen(false); setEditingTemplate(null); resetForm(); }} disabled={isSubmitting}>Cancel</Button>
        <Button onClick={() => handleSubmit(isEdit)} disabled={isSubmitting || !formData.name || formData.durationMonths < 1}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEdit ? "Update" : "Create"}
        </Button>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Templates</h2>
          <p className="text-[var(--secondary-text-color)] mb-4">Failed to load membership templates</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Membership Templates</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input
              className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center"
              type="text"
              placeholder="Search"
              value={searchQuery ?? ""}
              onChange={(e) => setPageState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))}
            />
          </div>
          <Button onClick={handleCreate} className="h-[32px] px-3 text-[13px] font-medium bg-[var(--primary-color)] text-white rounded-[4px] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Template
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-3 h-[calc(100vh-130px)]">
          {!paginatedTemplates.length && !isLoading ? (
            <NoRecordFound title="Membership Templates" description="There are no membership templates configured." />
          ) : (
            <CommonGrid
              rowData={paginatedTemplates}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Membership Template</DialogTitle></DialogHeader>
          {renderTemplateForm(false)}
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Membership Template</DialogTitle></DialogHeader>
          {renderTemplateForm(true)}
        </DialogContent>
      </Dialog>

      <ConfirmModal
        open={deleteModal}
        onClose={() => { setDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title="Delete Template"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="delete"
      />
    </div>
  );
};

export default MembershipTemplatesPage;
